import { TaskType } from '../types/shared';

export type TaskFieldType = 'space_select' | 'text' | 'textarea' | 'date' | 'number' | 'select';

export interface TaskFieldConfig {
    key: string;            // payload field name, e.g. 'targetSpaceId'
    type: TaskFieldType;
    labelKey: string;       // i18n translation key
    required: boolean;
    options?: { labelKey: string; value: string }[]; // For 'select' type
}

export interface TaskTypeConfig {
    buttonLabelKey: string;     // i18n key for the completion button
    buttonIcon?: string;        // MUI icon name (optional)
    fields: TaskFieldConfig[];  // form fields shown on completion (empty = no form)
    showSkipButton: boolean;    // whether to show "Not today" button
    showOverflowMenu: boolean;  // whether to show the ⋮ overflow menu
}

/**
 * Registry mapping each TaskType to its frontend configuration.
 *
 * To add a new task type:
 * 1. Add the type to TaskType in shared.ts
 * 2. Add a payload interface in shared.ts
 * 3. Add a registry entry here
 * 4. Add a handler in actions.ts (backend)
 * 5. Add the type to the AI prompt in tasks.ts
 * 6. Add translation keys in en/de/fr.json under "task_types"
 */
export const TASK_TYPE_REGISTRY: Record<TaskType, TaskTypeConfig> = {
    PLANT_SEED: {
        buttonLabelKey: 'task_types.plant_seed.button',
        fields: [
            { key: 'targetSpaceId', type: 'space_select', labelKey: 'task_types.space_label', required: true },
            {
                key: 'stage',
                type: 'select',
                labelKey: 'task_types.stage_label',
                required: true,
                options: [
                    { labelKey: 'task_types.stage_seedling', value: 'seedling' },
                    { labelKey: 'task_types.stage_plant', value: 'plant' }
                ]
            },
            { key: 'quantity', type: 'number', labelKey: 'task_types.quantity_label', required: false },
            { key: 'plantingDate', type: 'date', labelKey: 'task_types.planting_date_label', required: true },
        ],
        showSkipButton: true,
        showOverflowMenu: true,
    },
    MOVE_SEEDLING: {
        buttonLabelKey: 'task_types.move_seedling.button',
        fields: [
            { key: 'targetSpaceId', type: 'space_select', labelKey: 'task_types.space_label', required: true },
        ],
        showSkipButton: true,
        showOverflowMenu: true,
    },
    TRANSPLANT: {
        buttonLabelKey: 'task_types.transplant.button',
        fields: [
            { key: 'targetSpaceId', type: 'space_select', labelKey: 'task_types.space_label', required: true },
        ],
        showSkipButton: true,
        showOverflowMenu: true,
    },
    HARVEST: {
        buttonLabelKey: 'task_types.harvest.button',
        fields: [],
        showSkipButton: true,
        showOverflowMenu: true,
    },
    GENERIC: {
        buttonLabelKey: 'task_types.generic.button',
        fields: [],
        showSkipButton: true,
        showOverflowMenu: true,
    },
    ANSWER_QUESTION: {
        buttonLabelKey: 'task_types.answer_question.button',
        fields: [
            { key: 'answer', type: 'textarea', labelKey: 'task_types.answer_question.answer_label', required: true },
        ],
        showSkipButton: true,
        showOverflowMenu: true,
    },
    TIP: {
        buttonLabelKey: 'task_types.tip.button',
        fields: [],
        showSkipButton: false,
        showOverflowMenu: false,
    },
};

/**
 * Get the config for a given task type, falling back to GENERIC.
 */
export function getTaskTypeConfig(type: TaskType | string): TaskTypeConfig {
    return TASK_TYPE_REGISTRY[type as TaskType] || TASK_TYPE_REGISTRY.GENERIC;
}
